import { Badge, Button, Input, Table, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { CopyOutlined } from "@ant-design/icons";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { IOtpLogEntry } from "../../../models/otp/response";
import { successNotification } from "../../../utils/helpers/notification";
import styles from "./style.module.scss";

const { Search } = Input;
const { Text } = Typography;

interface OtpHistoryTableProps {
  data: IOtpLogEntry[];
  loading?: boolean;
}

const OtpHistoryTable: React.FC<OtpHistoryTableProps> = ({ data, loading = false }) => {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState("");

  const handleCopy = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      successNotification(t("code_copied"));
    } catch (error) {
      console.error("Failed to copy:", error);
    }
  };

  const formatDateTime = (isoString: string): string => {
    const date = new Date(isoString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");

    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  // Helper function to check if OTP is expired
  // An OTP is expired if:
  // 1. Time has passed (validUntil < now), OR
  // 2. There's a newer OTP for the same mobile number
  const isOtpExpired = (record: IOtpLogEntry): boolean => {
    const now = new Date().getTime();
    const expiryTime = new Date(record.validUntil).getTime();

    // Check if time expired
    if (now > expiryTime) {
      return true;
    }

    // Check if there's a newer OTP for the same mobile number
    const newerOtpExists = data.some((item) => {
      return (
        item.mobile === record.mobile &&
        new Date(item.createdAt).getTime() > new Date(record.createdAt).getTime()
      );
    });

    return newerOtpExists;
  };

  const columns: ColumnsType<IOtpLogEntry> = [
    {
      title: t("mobile_number"),
      dataIndex: "originalMobile",
      key: "originalMobile",
      width: 150,
      render: (originalMobile: string) => <Text strong>{originalMobile}</Text>,
    },
    {
      title: t("otp_code"),
      dataIndex: "otp",
      key: "otp",
      width: 120,
      render: (otp: string) => (
        <Text
          strong
          style={{ fontFamily: "monospace", fontSize: "16px", letterSpacing: "2px" }}
        >
          {otp}
        </Text>
      ),
    },
    {
      title: t("created_at"),
      dataIndex: "createdAt",
      key: "createdAt",
      width: 180,
      render: (createdAt: string) => formatDateTime(createdAt),
      sorter: (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      defaultSortOrder: "ascend",
    },
    {
      title: t("generated_by"),
      dataIndex: "generatedBy",
      key: "generatedBy",
      width: 150,
      render: (generatedBy: { id: number; name: string }) => (
        <Text>{generatedBy.name}</Text>
      ),
    },
    {
      title: t("status"),
      key: "status",
      width: 100,
      render: (_: any, record: IOtpLogEntry) => {
        const isExpired = isOtpExpired(record);
        return isExpired ? (
          <Badge status="default" text={t("expired")} />
        ) : (
          <Badge status="success" text={t("active")} />
        );
      },
      filters: [
        { text: t("active"), value: "active" },
        { text: t("expired"), value: "expired" },
      ],
      onFilter: (value, record) => {
        const expired = isOtpExpired(record);
        return value === "active" ? !expired : expired;
      },
    },
    {
      title: "",
      key: "action",
      width: 100,
      render: (_, record) => (
        <Button
          type="link"
          icon={<CopyOutlined />}
          onClick={() => handleCopy(record.otp)}
        >
          {t("copy_code")}
        </Button>
      ),
    },
  ];

  const filteredData = data.filter((item) =>
    item.originalMobile.toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <div className={styles.historyTable}>
      <Search
        placeholder={t("search_by_mobile")}
        onChange={(e) => setSearchText(e.target.value)}
        style={{ marginBottom: 16, maxWidth: 300 }}
        allowClear
      />
      <Table
        columns={columns}
        dataSource={filteredData}
        rowKey={(record) => record.id}
        loading={loading}
        pagination={{
          pageSize: 10,
          showSizeChanger: false,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} ${t("of")} ${total}`,
        }}
        scroll={{ x: 900 }}
      />
    </div>
  );
};

export default OtpHistoryTable;
